import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, ShoppingCart, Package, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesPoint = () => {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sale');
  
  // Sale state
  const [saleItems, setSaleItems] = useState([]);
  const [saleLoading, setSaleLoading] = useState(false);
  
  // Purchase Order state
  const [poItems, setPoItems] = useState([]);
  const [poSupplier, setPoSupplier] = useState('');
  const [poLoading, setPoLoading] = useState(false);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products?limit=15000`, { headers: getAuthHeader() });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Sale functions
  const addSaleItem = () => {
    setSaleItems([...saleItems, { product_id: '', product_name: '', quantity: 1, unit_price: 0, max_quantity: 0 }]);
  };

  const updateSaleItem = (index, field, value) => {
    const updated = [...saleItems];
    updated[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        updated[index].product_name = product.name;
        updated[index].unit_price = product.sell_price;
        updated[index].max_quantity = product.quantity;
      }
    }
    
    setSaleItems(updated);
  };

  const removeSaleItem = (index) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const calculateSaleTotal = () => {
    return saleItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const submitSale = async () => {
    if (saleItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const invalidItems = saleItems.filter(item => !item.product_id || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error('Please fill all item details correctly');
      return;
    }

    const overStockItems = saleItems.filter(item => item.quantity > item.max_quantity);
    if (overStockItems.length > 0) {
      toast.warning(`Stock will go negative for: ${overStockItems.map(i => i.product_name).join(', ')} — fix in Inventory later`);
    }

    setSaleLoading(true);
    try {
      await axios.post(`${API_URL}/sales`, {
        items: saleItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        total_amount: calculateSaleTotal()
      }, { headers: getAuthHeader() });

      toast.success('Sale completed successfully!');
      setSaleItems([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete sale');
    } finally {
      setSaleLoading(false);
    }
  };

  // Purchase Order functions
  const addPoItem = () => {
    setPoItems([...poItems, { product_id: '', product_name: '', quantity: 1, buy_price: 0 }]);
  };

  const updatePoItem = (index, field, value) => {
    const updated = [...poItems];
    updated[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        updated[index].product_name = product.name;
        updated[index].buy_price = product.buy_price;
      }
    }
    
    setPoItems(updated);
  };

  const removePoItem = (index) => {
    setPoItems(poItems.filter((_, i) => i !== index));
  };

  const calculatePoTotal = () => {
    return poItems.reduce((sum, item) => sum + (item.quantity * item.buy_price), 0);
  };

  const submitPurchaseOrder = async () => {
    if (poItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const invalidItems = poItems.filter(item => !item.product_id || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error('Please fill all item details correctly');
      return;
    }

    setPoLoading(true);
    try {
      await axios.post(`${API_URL}/purchase-orders`, {
        items: poItems,
        total_amount: calculatePoTotal(),
        supplier: poSupplier
      }, { headers: getAuthHeader() });

      toast.success('Purchase Order created! Stock updated.');
      setPoItems([]);
      setPoSupplier('');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    } finally {
      setPoLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-point-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
          Sales Point
        </h1>
        <p className="text-slate-500 mt-1">Create sales and purchase orders</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="sale" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="sale" data-testid="sale-tab" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            <ShoppingCart className="w-4 h-4 mr-2" /> New Sale
          </TabsTrigger>
          <TabsTrigger value="purchase" data-testid="purchase-tab" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            <Package className="w-4 h-4 mr-2" /> Purchase Order
          </TabsTrigger>
        </TabsList>

        {/* Sale Tab */}
        <TabsContent value="sale" className="mt-6">
          <Card className="border rounded-sm" data-testid="sale-card">
            <CardHeader className="border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg font-bold uppercase">Sale Items</CardTitle>
                <Button 
                  onClick={addSaleItem}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="add-sale-item"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-24">Qty</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-32">Unit Price</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-32">Subtotal</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {saleItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        Click "Add Item" to start a sale
                      </TableCell>
                    </TableRow>
                  ) : (
                    saleItems.map((item, index) => (
                      <TableRow key={index} data-testid={`sale-item-row-${index}`}>
                        <TableCell>
                          <Select 
                            value={item.product_id} 
                            onValueChange={(value) => updateSaleItem(index, 'product_id', value)}
                          >
                            <SelectTrigger data-testid={`sale-product-select-${index}`}>
                              <SelectValue placeholder="Select product" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.filter(p => p.quantity > 0).map(product => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name} ({product.quantity} in stock)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            max={item.max_quantity}
                            value={item.quantity}
                            onChange={(e) => updateSaleItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-20 text-center"
                            data-testid={`sale-quantity-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${item.unit_price.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          ${(item.quantity * item.unit_price).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeSaleItem(index)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`remove-sale-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {saleItems.length > 0 && (
                <div className="p-6 border-t bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="text-lg">
                      <span className="text-slate-600">Total:</span>
                      <span className="font-bold text-2xl text-slate-900 ml-3" data-testid="sale-total">
                        ${calculateSaleTotal().toFixed(2)}
                      </span>
                    </div>
                    <Button 
                      onClick={submitSale}
                      disabled={saleLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 px-8"
                      data-testid="complete-sale-button"
                    >
                      {saleLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 spinner" /> Processing...</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" /> Complete Sale</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Purchase Order Tab */}
        <TabsContent value="purchase" className="mt-6">
          <Card className="border rounded-sm" data-testid="purchase-order-card">
            <CardHeader className="border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg font-bold uppercase">Purchase Order</CardTitle>
                  <div className="mt-3">
                    <Label htmlFor="supplier" className="text-sm text-slate-600">Supplier Name (Optional)</Label>
                    <Input
                      id="supplier"
                      value={poSupplier}
                      onChange={(e) => setPoSupplier(e.target.value)}
                      placeholder="Enter supplier name"
                      className="mt-1 max-w-xs"
                      data-testid="po-supplier-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={addPoItem}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="add-po-item"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-24">Qty</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-32">Buy Price</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-right w-32">Subtotal</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {poItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                        <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        Click "Add Item" to create a purchase order
                      </TableCell>
                    </TableRow>
                  ) : (
                    poItems.map((item, index) => (
                      <TableRow key={index} data-testid={`po-item-row-${index}`}>
                        <TableCell>
                          <Select 
                            value={item.product_id} 
                            onValueChange={(value) => updatePoItem(index, 'product_id', value)}
                          >
                            <SelectTrigger data-testid={`po-product-select-${index}`}>
                              <SelectValue placeholder="Select product" />
                            </SelectTrigger>
                            <SelectContent>
                              {products.map(product => (
                                <SelectItem key={product.id} value={product.id}>
                                  {product.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updatePoItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-20 text-center"
                            data-testid={`po-quantity-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.buy_price}
                            onChange={(e) => updatePoItem(index, 'buy_price', parseFloat(e.target.value) || 0)}
                            className="w-28 text-right"
                            data-testid={`po-buy-price-${index}`}
                          />
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          ${(item.quantity * item.buy_price).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removePoItem(index)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`remove-po-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              {poItems.length > 0 && (
                <div className="p-6 border-t bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="text-lg">
                      <span className="text-slate-600">Total:</span>
                      <span className="font-bold text-2xl text-slate-900 ml-3" data-testid="po-total">
                        ${calculatePoTotal().toFixed(2)}
                      </span>
                    </div>
                    <Button 
                      onClick={submitPurchaseOrder}
                      disabled={poLoading}
                      className="bg-slate-900 hover:bg-slate-800 px-8"
                      data-testid="submit-po-button"
                    >
                      {poLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 spinner" /> Processing...</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" /> Create Purchase Order</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SalesPoint;
